import {
  Component, Input, Inject,
  OnInit, OnDestroy, ViewContainerRef,
  ComponentRef, OnChanges
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';

import { FieldConfig } from '../../models/field-config.model';
import {
  FIELD_REGISTRY,
  FieldRegistryMap,
  FieldComponent
} from '../../tokens/field-registry.token';
import { DataCatalogService } from '../../services/data-catalog.service';

/** Tipos de campo que admiten opciones desde un catálogo */
const OPTION_FIELD_TYPES = new Set(['select', 'radio', 'checkbox', 'autocomplete', 'multiselect']);

@Component({
  selector: 'app-dynamic-field',
  standalone: true,
  imports: [CommonModule],
  template: `<ng-container></ng-container>`
})
export class DynamicFieldComponent implements OnInit, OnChanges, OnDestroy {

  @Input() field!: FieldConfig;
  @Input() form!: FormGroup;

  private componentRef?: ComponentRef<FieldComponent>;
  private lastRenderedEffectiveType: string | null = null;
  private destroy$ = new Subject<void>();

  constructor(
    private vcr: ViewContainerRef,
    @Inject(FIELD_REGISTRY) private registry: FieldRegistryMap,
    private catalogService: DataCatalogService
  ) {}

  ngOnInit(): void {
    this.renderField();
  }

  ngOnChanges(): void {
    if (!this.componentRef) return;
    // Si el tipo efectivo cambió (ej: custom sin opciones → custom con catálogo)
    // hay que recrear el componente con el nuevo tipo.
    if (this.getEffectiveType() !== this.lastRenderedEffectiveType) {
      this.renderField();
    } else {
      this.updateInputs();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Determina el tipo efectivo para seleccionar el componente de renderizado.
   * Un campo `custom` con opciones o catalogId se trata como `select` o `multiselect`
   * según el valor de customProps.multiple.
   */
  private getEffectiveType(): string {
    if (
      this.field.type === 'custom' &&
      (this.field.options?.length || this.field.catalogId)
    ) {
      const props = this.field.customProps as Record<string, unknown> | undefined;
      return props?.['multiple'] ? 'multiselect' : 'select';
    }
    return this.field.type;
  }

  private renderField(): void {
    const effectiveType  = this.getEffectiveType();
    const componentType  = this.registry[effectiveType] ?? this.registry[this.field.type];

    if (!componentType) {
      console.warn(`[AdminForms] Tipo '${this.field.type}' no registrado`);
      return;
    }

    this.lastRenderedEffectiveType = effectiveType;
    this.vcr.clear();
    this.componentRef = this.vcr.createComponent(componentType);
    this.updateInputs();
  }

  private updateInputs(): void {
    if (!this.componentRef) return;

    const instance = this.componentRef.instance;
    instance.control = this.form.get(this.field.id) as FormControl;

    // Pasar el FormGroup completo para que campos especiales (ej. fecha de nacimiento)
    // puedan actualizar controles hermanos del formulario.
    // Nota: NO usar 'form' in instance — Angular Ivy no inicializa propiedades @Input()
    // opcionales como propiedades propias hasta que se asignan, así que 'in' devuelve false.
    (instance as unknown as { form: FormGroup }).form = this.form;

    // Asignar siempre el campo antes del primer detectChanges para evitar
    // que el template acceda a `field.cssClass` con field === undefined.
    instance.field = this.field;
    this.componentRef.changeDetectorRef.detectChanges();

    // Incluir 'custom' cuando tiene catalogId para que se carguen las opciones
    const needsCatalog =
      !!this.field.catalogId &&
      (OPTION_FIELD_TYPES.has(this.field.type) || this.field.type === 'custom');

    if (needsCatalog) {
      // Si el campo ya tiene opciones guardadas en el schema, usarlas directamente
      // para respetar las ediciones del usuario (agregar/borrar ítems en el builder).
      // Solo se carga del catálogo si el campo no tiene opciones propias.
      if (this.field.options?.length) {
        // Las opciones ya están en el schema; no hay nada que hacer.
      } else {
        this.catalogService.getItems(this.field.catalogId!)
          .pipe(takeUntil(this.destroy$))
          .subscribe(items => {
            instance.field = { ...this.field, options: items };
            this.componentRef?.changeDetectorRef.detectChanges();
          });
      }
    }
  }
}